import { useCallback } from "react";
import { formatValue, asNumber } from "../../lib/kernelFormat.js";
import { CanvasSurface, KernelEmpty, resolveCssColor } from "./shared.jsx";

export const kind = "donut";

// Single-ratio donut. Resolves value + max from data, then renders a
// thick ring with the percentage in the center. Doesn't try to do
// multi-segment pies — kernels with a true breakdown should use
// "bars" or "treemap" instead.
export function Component({ spec, data, fillHeight = false }) {
    const value = asNumber(pick(data, spec.valueField));
    const explicitMax = asNumber(pick(data, spec.maxField)) ?? asNumber(spec.max);
    // If max wasn't supplied, infer from the value magnitude: values
    // ≤1 are treated as 0-1 ratios; everything else as 0-100 (and a
    // value above 100 with no max is its own ratio of 100).
    const max = explicitMax != null ? explicitMax
        : (value == null ? 1 : (Math.abs(value) <= 1 ? 1 : 100));
    if (value == null) return <KernelEmpty message={spec.empty || "no data"} />;

    const ratio = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
    const height = Math.max(80, Math.min(280, Number(spec.height) || 160));
    const color = spec.color || "var(--green)";

    const draw = useCallback((ctx, { width, height: h, root }) => {
        const c = resolveCssColor(root, color);
        const bg = resolveCssColor(root, "var(--line-soft)") || "#e5e2dc";
        const cx = width / 2;
        const cy = h / 2;
        // Pick stroke first, then back the radius off by half-stroke so
        // the ring's OUTER edge sits inside the canvas (was: outer edge
        // = r + stroke/2, which overflowed the container).
        const minDim = Math.min(width, h);
        const stroke = Math.max(8, Math.min(22, minDim * 0.11));
        const r = Math.max(0, minDim / 2 - stroke / 2 - 4);

        ctx.lineCap = "butt";
        ctx.lineWidth = stroke;

        // background ring
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.strokeStyle = bg;
        ctx.stroke();

        // value arc
        if (ratio > 0) {
            ctx.beginPath();
            ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * ratio);
            ctx.strokeStyle = c;
            ctx.stroke();
        }
    }, [color, ratio]);

    const centerText = spec.format
        ? formatValue(value, spec.format)
        : `${(ratio * 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}%`;

    return (
        <div className={`rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[12px] py-[10px]${fillHeight ? " flex h-full min-h-0 flex-col" : ""}`}>
            {spec.label ? (
                <div className="mb-[4px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                    {spec.label}
                </div>
            ) : null}
            <div
                className={`relative${fillHeight ? " flex-1 min-h-0" : ""}`}
                style={{
                    ...(fillHeight ? {} : { height: `${height}px` }),
                    containerType: "size",
                }}
            >
                <CanvasSurface
                    draw={draw}
                    deps={[ratio, color]}
                    className="absolute inset-0"
                />
                <div className="pointer-events-none absolute inset-0 m-auto flex size-[min(60cqw,60cqh)] flex-col items-center justify-center text-center">
                    <div className="font-mono text-[18px] leading-[1.05] text-[color:var(--ink)]">
                        {centerText}
                    </div>
                    {spec.sublabel ? (
                        <div
                            className="pointer-events-auto mt-[2px] line-clamp-3 text-[clamp(8px,6cqw,10px)] leading-[1.25] text-[color:var(--muted-soft)]"
                            style={{ overflowWrap: "anywhere" }}
                            title={spec.sublabel}
                        >
                            {spec.sublabel}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function pick(data, field) {
    if (data == null) return null;
    if (typeof data === "number") return data;
    if (typeof data === "object" && !Array.isArray(data)) {
        if (field) return data[field];
        return data.value ?? null;
    }
    return null;
}
